import { useCallback, useEffect, useRef, useState } from "react"
import styles from './index.module.scss'
import { PlusOutlined, LoadingOutlined, ProfileOutlined, UploadOutlined } from '@ant-design/icons';
import { Form, Image, Input, Popover, Space, Upload, message } from "antd";
import * as _ from "underscore";
import Utils from '@utils/utils'
import {forwardRef, useImperativeHandle} from 'react'
import API from '@api/api'
import UploadRequirements from "../UploadRequirements"
import {RESOURCE_TYPE} from '@utils/CONST'

const ImageCard = (props) => {
    const handleRemove = () => {
        props.onRemove()
    }
    return (
        <div className={styles.ImageCard}>
            <Image
                preview={false}
                width={'100%'}
                height={'100%'}
                src={Utils.getImgUrlWithWebp(props.value)}
            />
            <i className={styles.del_btn} onClick={handleRemove}></i>
        </div>
    )
}

const UploadImage = (props) => {
    const [maxCount, setMaxCount] = useState(props.maxCount)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        setMaxCount(props.maxCount)
    }, [props.maxCount])

    // 1.易盾审核
    // 2.公众人物检测（上传接口增加bool figureCheck）
    // 3.不支持多人照（接人脸检测/body检测，是否为单张人脸/body）——创建constantID时处理
    // 4.头肩照（需要接body检测）——创建constantID时处理
    // 5.为同一人（要接人脸相似度检测，face ID，判断是否为同一人）——创建constantID时处理
    const onUploadFileList = useCallback(_.debounce(async (fileList) => {
        setLoading(true)
        fileList = fileList.slice(0, maxCount)
        for (let i = 0; i < fileList.length; i++) {
            try {
                console.log(`处理第${i}张图片`)
                let file = fileList[i]
                // 必须上传图片
                const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp'|| file.type === 'image/avif';
                if (!isJpgOrPng) throw new Error('您只能上传JPG/PNG/WEBP/AVIF格式的文件')
                const validate = await Utils.validateImageFile(file)
                if(!validate) continue;
                // 上传图片resize到4096
                const resizeRes = await Utils.resizeImageFileMaxByAliyunBy(file, 4096)
                if (!resizeRes || !resizeRes.success) {message.warning(resizeRes?.message);continue;}
                file = resizeRes.file
                // 易盾审核+公众人物检测
                const res = await Utils.checkImageIslegal(file, 0, null, true)
                if (!res) continue
                if (!res.isLegal) throw new Error('经检测，您上传的内容涉嫌违规，已进行屏蔽处理，人工审核会对图片进行复核。')
                props.onUpload(res.data)
            } catch (err) {
                message.warning(err.message)
            }
        }
        setLoading(false)
    }, 200), [maxCount])

    const handleBeforeUpload = async (file, fileList) => {
        onUploadFileList(fileList)
        return false
    }
    const handleDrop = async (e) => {
        if ("getFilesAndDirectories" in e.dataTransfer) {
            const filesAndDirs = await e.dataTransfer.getFilesAndDirectories()
            const fileList = await Utils.getFilesFromDirs(filesAndDirs, "/")
            onUploadFileList(fileList)
        }
    }

    return (
        <Upload.Dragger
            disabled={loading}
            className={styles.UploadWrap}
            accept='image/jpeg,image/png,image/webp,image/avif'
            listType="picture-card"
            showUploadList={false}
            multiple
            maxCount={maxCount}
            beforeUpload={handleBeforeUpload}
            onDrop={handleDrop}
        > 
            <div className={styles.UploadContent}>
                {loading ? <LoadingOutlined /> : <UploadOutlined />}
                <p>上传图片</p>
            </div>
        </Upload.Dragger>
    )
}

const CustomModelForm = forwardRef((props, ref) => {
    const [form] = Form.useForm()
    const items = Form.useWatch(['items'], form)

    const initialValues = {
        name: "",
        items: []
    }

    useImperativeHandle(ref, () => ({
        onSubmit
    }))

    const onSubmit = async () => {
        let values
        try {
            values = await form.validateFields()
        } catch (err) {
            return false
        }
        try {
            const { name, items, singleImage } = values
            const res2 = await API.saveHsfModel({
                modelName: name,
                cover: singleImage[0].url,
                imageUrls: items.map(item => item.url)
            })
            if (res2.code != 0) {
                // 处理错误信息展示
                if (res2.data) {
                    const errorMessages = []
                    if (res2.data.cover) {
                        errorMessages.push(`封面图错误: ${res2.data.cover}`)
                    }
                    for (let i = 0; i < items.length; i++) {
                        if (res2.data[i]) {
                            errorMessages.push(`第${i+1}张训练图错误: ${res2.data[i]}`)
                        }
                    }
                    throw new Error(errorMessages.join('\n'))
                } else {
                    throw new Error(res2.message || '请求参数不合法')
                }
            } else {
                return res2.data
            }
        } catch (err) {
            console.error(err)
            throw err
        }
    }

    return (
        <div className={styles.CustomModelFormWrap}>
            <div className={styles.CustomModelTipsBox}>
                <ul className={styles.CustomModelTips}>
                    <li>如上传照片非您本人，请确保您已获得图片肖像权利人的合法授权。</li>
                    <li>如上传照片为AI生成虚拟模特，请确保您已获得模特产出平台的虚拟模特肖像授权。</li>
                    <li>请勿上传公众人物（文体明星、政治人物等）照片。</li>
                </ul>
            </div>
            <Form
                autoComplete="off"
                form={form}
                initialValues={initialValues}
                layout="vertical"
            >
                <Form.Item
                    label="模特名称"
                    name="name"
                    rules={[
                        { required: true, message: '请输入模特名称', }
                    ]}
                >
                    <Input
                        placeholder="请输入您的模特名称"
                        allowClear
                        showCount
                        maxLength={20}
                    />
                </Form.Item>
                <Form.Item
                    className={styles.SingleImageItem}
                    label={
                        <>
                        <div className={styles.SingleImageLabel}><span>模特封面图1张</span></div>
                        <Popover
                            classNames={{ root: styles.RefImgPopover }}
                            placement="bottomRight"
                            arrow={false}                        
                            content={<UploadRequirements size="small" resourceType={RESOURCE_TYPE.MODEL} />}
                        >
                            <div className={styles.SingleImageRequire}><span>上传图片要求</span><ProfileOutlined /></div>
                        </Popover>
                        </>
                    }
                    required
                >
                    <Form.List
                        name="singleImage"
                        rules={[{
                            validator: async (_, message) => {
                                if (!message || message.length == 0) return Promise.reject(new Error('请上传至少一张封面图'));
                            }
                        }]}
                    >
                    {(fields, opt, {errors}) => (
                        <>
                        <div className={styles.SingleImageTips}>
                            请上传露出人物完整五官，正面视角，胸部以上的大头照。后续将以此图作为基准。 
                        </div>
                        <div style={{display: 'flex', columnGap: 8}}>
                        {
                            fields.map(field => (
                                <Form.Item key={field.key} noStyle name={[field.name, 'url']}>
                                    <ImageCard onRemove={() => opt.remove(field.name)} />
                                </Form.Item>
                            ))
                        }
                        {
                            fields.length < 1 &&
                            <UploadImage key="singleImage" maxCount={1} onUpload={url => opt.add({url})} />
                        }
                        </div>
                        <div style={{position: "absolute", bottom: 0, transform: 'translate(0, 100%)'}}>
                            <Form.ErrorList style={{width: '100%'}} errors={errors}/>
                        </div>
                        </>
                    )}
                    </Form.List>
                </Form.Item>
                <Form.Item
                    label={
                        <div>
                            <span>模特训练图1~4张</span>
                        </div>
                    }
                    // required
                >
                    <Form.List
                        name="items"
                        // rules={[{
                        //     validator: async (_, message) => {
                        //         if (!message || message.length == 0) return Promise.reject(new Error('请上传至少一张模特训练图'));
                        //     }
                        // }]}
                    >
                    {(fields, opt, {errors}) => (
                        <>
                        <div className={styles.SingleImageTips}>
                            可上传同一人不同角度大头照 
                        </div>
                        <div style={{display: 'flex', columnGap: 8}}>
                        {
                            fields.map(field => (
                                <Form.Item key={field.key} noStyle name={[field.name, 'url']}>
                                    <ImageCard onRemove={() => opt.remove(field.name)} />
                                </Form.Item>
                            ))
                        }
                        {
                            fields.length < 4 &&
                            Array(4-fields?.length).fill("").map((_, index) => (
                                <UploadImage key={index} maxCount={4-fields.length} onUpload={url => opt.add({url})} />
                            ))
                        }
                        </div>
                        <div style={{position: "absolute", bottom: 0, transform: 'translate(0, 100%)'}}>
                            <Form.ErrorList style={{width: '100%'}} errors={errors}/>
                        </div>
                        </>
                    )}
                    </Form.List>
                </Form.Item>
                <div className={styles.Protocol}>
                    点击确定即表示您已阅读并同意本产品的
                    <a target='_blank' href='/服务协议.htm'>服务协议</a>
                    和
                    <a target='_blank' href='/隐私政策.htm'>隐私政策</a>
                </div>
            </Form>
        </div>
    )
})

export default CustomModelForm